import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiArrowRightBold, PiCaretDownBold, PiCheckBold, PiPaletteBold, PiWarningBold, PiWarningCircleFill } from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { Switch } from '@v2/design-system';
import { ColorDot } from '@v2/features/colors/ColorDot';
import type { ColorHexMaps, ColorSlot } from '@v2/features/colors/colorHex';

type Category = 'frame' | 'seat' | 'stitches' | 'arm';

type Occurrence = { item_id: number; order: string | null; sku: string | null; product: string | null };

type Issue = {
    type: 'off_palette' | 'not_accepted';
    category: Category;
    category_label: string;
    value: string;
    suggestions?: string[];
    official?: string;
    product?: string | null;
    sku?: string | null;
    accepted?: string[];
    occurrences: Occurrence[];
};

type Row = {
    item_id: number;
    order: string | null;
    sku: string | null;
    product: string | null;
    image_url: string | null;
    quantity: number;
    unit_price_usd: number;
    parts: Record<Category, { ploomes: string | null; hub: string | null; same: boolean }>;
    diverging_parts: Category[];
    status: 'match' | 'diverge' | 'no_order_line';
};

type Check = {
    ok: boolean;
    issues: Issue[];
    rows: Row[];
    summary: { match: number; diverge: number; no_order_line: number; off_palette: number; not_accepted: number } | null;
    checked_lines: number;
};

const PARTS: { key: Category; slot: ColorSlot; label: string }[] = [
    { key: 'frame', slot: 'frame', label: 'Estrutura' },
    { key: 'seat', slot: 'upholstery', label: 'Estofado' },
    { key: 'stitches', slot: 'stitches', label: 'Costura' },
    { key: 'arm', slot: 'arm', label: 'Braço' },
];

const usd = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

function Dots({ row, side, hexMaps }: { row: Row; side: 'ploomes' | 'hub'; hexMaps: ColorHexMaps }) {
    return (
        <div className="flex items-center gap-1.5">
            {PARTS.map((p) => {
                const part = row.parts[p.key];
                const name = part[side];
                return (
                    <span
                        key={p.key}
                        className={cn('rounded-full p-0.5', !part.same && 'bg-amber-100 ring-2 ring-amber-400 dark:bg-amber-500/20')}
                    >
                        <ColorDot slot={p.slot} name={name} hexMaps={hexMaps} size="md" title={`${p.label}: ${name ?? 'sem customização'}`} />
                    </span>
                );
            })}
        </div>
    );
}

/**
 * Conferência visual das cores: pedido no Ploomes × linha da PO no Hub (o que vai no PDF ao
 * fornecedor), mais grafias fora do cadastro e cores que o produto não aceita. Aparece sozinha.
 */
export default function PoColorCheckPanel({
    shipmentId,
    signature,
    closed,
    hexMaps,
    onFixed,
}: {
    shipmentId: number;
    /** Muda quando as linhas/cores da PO mudam: refaz a conferência. */
    signature: string;
    closed: boolean;
    hexMaps: ColorHexMaps;
    onFixed: () => void;
}) {
    const qc = useQueryClient();
    const [open, setOpen] = useState<boolean | null>(null);
    const [onlyDiverging, setOnlyDiverging] = useState<boolean | null>(null);
    const [choice, setChoice] = useState<Record<string, string>>({});
    const [remember, setRemember] = useState<Record<string, boolean>>({});
    const key = ['po-color-check', shipmentId, signature];

    const check = useQuery({
        queryKey: key,
        queryFn: () => api.get<{ data: Check }>(`/shipments/${shipmentId}/purchase-order/editor/color-check`).then((r) => r.data.data),
    });

    const onDone = (res: { message: string; data: { check: Check } }) => {
        toast.success(res.message);
        qc.setQueryData(key, res.data.check);
        onFixed();
    };
    const onFail = (err: { response?: { data?: { message?: string } } }) =>
        toast.error(err?.response?.data?.message ?? 'Não foi possível corrigir.');

    const fix = useMutation({
        mutationFn: async (payload: { category: string; from: string; to: string; create_alias: boolean }) =>
            (await api.post(`/shipments/${shipmentId}/purchase-order/editor/fix-color`, payload)).data,
        onSuccess: onDone,
        onError: onFail,
    });
    const revert = useMutation({
        mutationFn: async (payload: { item_id: number; category: Category }) =>
            (await api.post(`/shipments/${shipmentId}/purchase-order/editor/use-order-color`, payload)).data,
        onSuccess: onDone,
        onError: onFail,
    });

    const data = check.data;
    if (!data || data.checked_lines === 0) return null;

    const s = data.summary ?? { match: 0, diverge: 0, no_order_line: 0, off_palette: 0, not_accepted: 0 };
    // Aberto por padrão: ver que as cores batem também é informação (e é o que o Comex confere).
    const isOpen = open ?? true;
    const filter = onlyDiverging ?? s.diverge > 0;
    const rows = filter ? data.rows.filter((r) => r.status === 'diverge') : data.rows;
    const off = data.issues.filter((i) => i.type === 'off_palette');
    const notAccepted = data.issues.filter((i) => i.type === 'not_accepted');
    const issueKey = (i: Issue) => `${i.category}|${i.value.toLowerCase()}`;
    const refs = (i: Issue) =>
        Array.from(new Set(i.occurrences.map((o) => [o.order ? `#${o.order}` : null, o.sku].filter(Boolean).join(' ')))).slice(0, 5).join(', ') +
        (i.occurrences.length > 5 ? ` +${i.occurrences.length - 5}` : '');

    return (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <header className="flex flex-wrap items-center gap-3 px-4 py-3">
                <button type="button" onClick={() => setOpen(!isOpen)} className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={isOpen}>
                    <PiPaletteBold className="size-5 shrink-0 text-gray-500" />
                    <span className="min-w-0">
                        <span className="block text-sm font-bold text-gray-900 dark:text-gray-100">Conferência de cores</span>
                        <span className="block text-[11px] text-gray-500">
                            Cores do pedido no Ploomes × cores da PO no Hub (as que vão no PDF ao fornecedor)
                        </span>
                    </span>
                    <PiCaretDownBold className={cn('ms-1 size-3.5 shrink-0 text-gray-400 transition-transform', !isOpen && '-rotate-90')} />
                </button>
                <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                        <PiCheckBold className="size-3.5" /> {s.match} conferem
                    </span>
                    {s.diverge > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200">
                            <PiWarningBold className="size-3.5" /> {s.diverge} divergência(s)
                        </span>
                    )}
                    {s.off_palette > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
                            <PiWarningCircleFill className="size-3.5" /> {s.off_palette} fora do cadastro
                        </span>
                    )}
                    {s.not_accepted > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
                            {s.not_accepted} não aceita(s) pelo produto
                        </span>
                    )}
                </div>
            </header>

            {isOpen && (
                <div className="border-t border-gray-100 dark:border-gray-700">
                    {(off.length > 0 || notAccepted.length > 0) && (
                        <div className="space-y-2 border-b border-gray-100 bg-rose-50/40 px-4 py-3 dark:border-gray-700 dark:bg-rose-500/5">
                            {off.length > 0 && (
                                <p className="text-[11px] text-rose-800 dark:text-rose-200">
                                    Grafia que não existe na paleta nem no de-para. O PDF imprime exatamente esse texto — corrija para o nome oficial.
                                </p>
                            )}
                            {off.map((i) => {
                                const k = issueKey(i);
                                const target = choice[k] ?? i.suggestions?.[0] ?? '';
                                return (
                                    <div key={k} className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs dark:bg-gray-900">
                                        <span className="w-20 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{i.category_label.split(' ')[0]}</span>
                                        <span className="font-mono font-semibold text-rose-700 dark:text-rose-300">"{i.value}"</span>
                                        <PiArrowRightBold className="size-3 text-gray-400" />
                                        {i.suggestions && i.suggestions.length > 0 ? (
                                            <select
                                                value={target}
                                                onChange={(e) => setChoice({ ...choice, [k]: e.target.value })}
                                                disabled={closed}
                                                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs font-semibold dark:border-gray-700 dark:bg-gray-900"
                                            >
                                                {i.suggestions.map((sug) => (
                                                    <option key={sug} value={sug}>
                                                        {sug}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className="text-gray-500">sem cor parecida — cadastre em Cores de produto</span>
                                        )}
                                        <span className="min-w-0 flex-1 truncate text-[11px] text-gray-500" title={refs(i)}>
                                            {i.occurrences.length} linha(s): {refs(i)}
                                        </span>
                                        {!closed && target && (
                                            <>
                                                <div className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300" title="Os próximos pedidos com essa grafia já entram com a cor oficial">
                                                    <Switch
                                                        size="sm"
                                                        checked={remember[k] ?? true}
                                                        onChange={(e) => setRemember({ ...remember, [k]: e.target.checked })}
                                                        aria-label="Salvar de-para"
                                                        className="shrink-0 [&_label]:my-0"
                                                    />
                                                    salvar de-para
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled={fix.isPending}
                                                    onClick={() => fix.mutate({ category: i.category, from: i.value, to: target, create_alias: remember[k] ?? true })}
                                                    className="h-8 rounded-md bg-rose-600 px-3 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                                                >
                                                    Corrigir na PO
                                                </button>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                            {notAccepted.map((i) => (
                                <p key={`${issueKey(i)}|${i.sku}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs dark:bg-gray-900">
                                    <PiWarningBold className="size-4 shrink-0 text-rose-600" />
                                    <span>
                                        <strong>{i.product ?? i.sku}</strong> não aceita {i.category_label.toLowerCase()} <strong>{i.official}</strong>
                                        <span className="text-gray-500"> · aceita: {(i.accepted ?? []).join(', ')}</span>
                                    </span>
                                    <span className="ms-auto text-[11px] text-gray-500">{i.occurrences.length} linha(s): {refs(i)}</span>
                                </p>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-2 px-4 py-2 text-[11px] text-gray-500">
                        <span>
                            {filter ? `Mostrando ${rows.length} linha(s) com divergência` : `${rows.length} linha(s)`}
                            {s.no_order_line > 0 && ` · ${s.no_order_line} sem pedido do Ploomes para comparar`}
                        </span>
                        <button type="button" onClick={() => setOnlyDiverging(!filter)} className="font-semibold text-primary hover:underline">
                            {filter ? `Ver todas (${data.rows.length})` : s.diverge > 0 ? 'Só divergências' : ''}
                        </button>
                    </div>

                    <div className="max-h-[26rem] overflow-auto">
                        <table className="w-full min-w-[56rem] text-left text-sm">
                            <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-300">
                                <tr>
                                    <th className="w-10 px-3 py-2" />
                                    <th className="px-3 py-2 font-semibold">Produto</th>
                                    <th className="w-14 px-3 py-2 text-center font-semibold">Qtd.</th>
                                    <th className="px-3 py-2 font-semibold">
                                        Cores Ploomes
                                        <span className="block text-[10px] font-normal text-gray-400">Estrutura · Estofado · Costura · Braço</span>
                                    </th>
                                    <th className="px-3 py-2 font-semibold">
                                        Cores Hub (PO)
                                        <span className="block text-[10px] font-normal text-gray-400">Estrutura · Estofado · Costura · Braço</span>
                                    </th>
                                    <th className="w-36 px-3 py-2 font-semibold">Conferência</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => {
                                    const diverge = row.status === 'diverge';
                                    return (
                                        <tr key={row.item_id} className={cn('border-t border-gray-100 align-middle dark:border-gray-700', diverge && 'bg-amber-50/70 dark:bg-amber-500/5')}>
                                            <td className="px-3 py-2 font-mono text-xs text-gray-400">{String(idx + 1).padStart(2, '0')}</td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-3">
                                                    <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-white dark:border-gray-700">
                                                        {row.image_url ? <img src={row.image_url} alt="" className="size-full object-contain p-0.5" loading="lazy" /> : null}
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block truncate font-semibold text-gray-900 dark:text-gray-100">{row.product}</span>
                                                        <span className="block text-[11px] text-gray-500">
                                                            {row.sku} · {usd(row.unit_price_usd)}/un{row.order ? ` · pedido ${row.order}` : ''}
                                                        </span>
                                                    </span>
                                                </div>
                                                {diverge && (
                                                    <div className="mt-2 space-y-1">
                                                        {row.diverging_parts.map((cat) => {
                                                            const p = PARTS.find((x) => x.key === cat)!;
                                                            const part = row.parts[cat];
                                                            return (
                                                                <div key={cat} className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-xs dark:border-amber-500/30 dark:bg-gray-900">
                                                                    <span className="font-semibold">{p.label}:</span>
                                                                    <ColorDot slot={p.slot} name={part.ploomes} hexMaps={hexMaps} size="md" />
                                                                    <span>
                                                                        <span className="text-[10px] text-gray-400">Ploomes</span> {part.ploomes ?? 'sem customização'}
                                                                    </span>
                                                                    <PiArrowRightBold className="size-3 text-gray-400" />
                                                                    <ColorDot slot={p.slot} name={part.hub} hexMaps={hexMaps} size="md" />
                                                                    <span>
                                                                        <span className="text-[10px] text-gray-400">Hub</span> {part.hub ?? 'sem customização'}
                                                                    </span>
                                                                    {!closed && (
                                                                        <button
                                                                            type="button"
                                                                            disabled={revert.isPending}
                                                                            onClick={() => revert.mutate({ item_id: row.item_id, category: cat })}
                                                                            title="Troca a cor da PO pela que veio do pedido. Se a PO foi corrigida de propósito (ex.: combinado com o cliente), mantenha."
                                                                            className="ms-auto rounded-md border border-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
                                                                        >
                                                                            Usar a do Ploomes
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-center font-semibold tabular-nums">{row.quantity}</td>
                                            <td className="px-3 py-2">{row.status === 'no_order_line' ? <span className="text-[11px] text-gray-400">sem pedido</span> : <Dots row={row} side="ploomes" hexMaps={hexMaps} />}</td>
                                            <td className="px-3 py-2">
                                                <Dots row={row} side="hub" hexMaps={hexMaps} />
                                            </td>
                                            <td className="px-3 py-2">
                                                {diverge ? (
                                                    <span className="inline-flex flex-col items-start">
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                                                            <PiWarningBold className="size-3.5" /> Divergem
                                                        </span>
                                                        <span className="mt-0.5 ps-1 text-[11px] text-gray-500">
                                                            {row.diverging_parts.map((c) => PARTS.find((x) => x.key === c)!.label).join(', ')}
                                                        </span>
                                                    </span>
                                                ) : row.status === 'match' ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                                                        <PiCheckBold className="size-3.5" /> Conferem
                                                    </span>
                                                ) : (
                                                    <span className="text-[11px] text-gray-400">sem pedido para comparar</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-3 py-6 text-center text-xs text-gray-500">
                                            Nenhuma divergência entre o Ploomes e a PO.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {closed && <p className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500 dark:border-gray-700">PO fechada: reabra para corrigir.</p>}
                </div>
            )}
        </section>
    );
}
